import { allFeatureIds } from './specimenFeatures';
import type { DichotomousKey } from './dichotomousKey';

export interface KeyValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
  reachedSpecimenIds: string[];
}

/** Checks key structure, binary contrast, reachability, cycles, and exact specimen coverage. */
export function validateDichotomousKey(key: DichotomousKey, expectedSpecimenIds: string[]): KeyValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const reachedSpecimenIds: string[] = [];
  if (!key.rootNodeId || !key.nodes[key.rootNodeId]) errors.push('The key root must reference an existing node.');
  const validFeatures = new Set(allFeatureIds());
  const visiting = new Set<string>();
  const visited = new Set<string>();

  const visit = (nodeId: string) => {
    if (visiting.has(nodeId)) { errors.push(`A loop returns to node “${nodeId}”.`); return; }
    if (visited.has(nodeId)) return;
    const node = key.nodes[nodeId];
    if (!node) { errors.push(`Referenced node “${nodeId}” does not exist.`); return; }
    visiting.add(nodeId);
    if (node.type === 'terminal') {
      reachedSpecimenIds.push(node.specimenId);
    } else {
      if (!node.statementYes.trim() || !node.statementNo.trim()) errors.push(`Couplet “${node.id}” needs both contrasting statements.`);
      if (node.statementYes.trim().toLowerCase() === node.statementNo.trim().toLowerCase()) errors.push(`Couplet “${node.id}” repeats the same statement on both branches.`);
      if (!validFeatures.has(node.condition.featureId)) errors.push(`Couplet “${node.id}” refers to unknown feature “${node.condition.featureId}”.`);
      if (!node.yesNextId || !node.noNextId || node.yesNextId === node.noNextId) errors.push(`Couplet “${node.id}” must lead to two distinct branches.`);
      visit(node.yesNextId);
      visit(node.noNextId);
    }
    visiting.delete(nodeId);
    visited.add(nodeId);
  };

  if (key.rootNodeId && key.nodes[key.rootNodeId]) visit(key.rootNodeId);
  Object.keys(key.nodes).filter((id) => !visited.has(id)).forEach((id) => warnings.push(`Node “${id}” is unreachable from the root.`));

  const counts = new Map<string, number>();
  reachedSpecimenIds.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
  expectedSpecimenIds.forEach((id) => {
    const count = counts.get(id) || 0;
    if (count === 0) errors.push(`The key does not identify expected specimen “${id}”.`);
    if (count > 1) errors.push(`The key can identify specimen “${id}” more than once.`);
  });
  reachedSpecimenIds.filter((id) => !expectedSpecimenIds.includes(id)).forEach((id) => errors.push(`The key includes unexpected specimen “${id}”.`));
  return { valid: errors.length === 0, errors, warnings, reachedSpecimenIds };
}
